import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SignalSparkline from './SignalSparkline';
import { SIGNAL_REFRESH_MS } from './signalRefresh';
import { useNavigate, useLocation } from 'react-router-dom';
import DeploySignalWizard from './DeploySignalWizard';
import CopierPanel from './CopierPanel';
import AlertSettingsPanel from './AlertSettingsPanel';
import { useConfirm } from './ConfirmDialog';
import {
  listDeployments,
  getDeployment,
  updateDeployment,
  deleteDeployment,
  listNotifications,
  listFollows,
  listSubscriptions,
  formatSignalMoney,
  summarizeSignalTrades,
} from './signalDataFeeds';

// Signals tab — the home for a user's deployed strategies.
//
// The design goal is that this feels ALIVE. A dashboard of static rows reads as dead, so each
// card carries a live equity sparkline, an open-position indicator, and a countdown to the next
// evaluation. The countdown in particular does more for perceived liveness than anything else
// here: it makes clear the thing is running even when no trade has fired for days.

// The tab split.
//
// Everything here used to be one column: alert channels, the signal grid, followed signals, the
// whole MT5 copier setup, then the alert feed. Each section is reasonable on its own and the page
// was several screens of scrolling, which buries the copier — the part with the most setup steps
// and the most to go wrong — under a list that grows every time a signal is deployed.
//
// Three panels because there are three jobs: watching what is running, wiring MetaTrader up to
// it, and deciding how you are told. `panel` in the URL so the copier can be linked to directly;
// the EA's own error messages tell people to go to "Dashboard -> Signals -> MT5 copier", and that
// should be one click rather than a scroll.
const SIGNAL_PANELS = [
  { id: 'signals', label: 'My Signals' },
  { id: 'copier', label: 'Copy to MT5' },
  { id: 'alerts', label: 'Alerts' },
];

const TIMEFRAME_MINUTES = {
  M1: 1, M5: 5, M15: 15, M30: 30, H1: 60, H4: 240, D1: 1440, W1: 10080,
};

// Time until the current candle closes — the moment this signal is next evaluated.
function useNextEvaluation(timeframe) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const minutes = TIMEFRAME_MINUTES[timeframe] || 60;
  const periodMs = minutes * 60 * 1000;
  const remaining = periodMs - (now % periodMs);
  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${String(mins).padStart(2, '0')}m`;
  if (mins > 0) return `${mins}m ${String(secs).padStart(2, '0')}s`;
  return `${secs}s`;
}

function StatusPill({ status, error }) {
  if (error) return <span className="signal-pill danger" title={error}>Error</span>;
  if (status === 'running') return <span className="signal-pill ok">Running</span>;
  if (status === 'paused') return <span className="signal-pill warn">Paused</span>;
  return <span className="signal-pill">Stopped</span>;
}

function DeploymentCard({ deployment, detail, onOpen, onToggle, onDelete }) {
  const countdown = useNextEvaluation(deployment.timeframe);
  const equity = useMemo(() => detail?.equity || [], [detail]);
  const allTrades = useMemo(() => detail?.trades || [], [detail]);

  // Seed history exists only to make the chart informative on day one. Counting it as
  // performance would overstate every brand-new signal, so the headline figures measure the
  // LIVE portion only and the seed count is shown separately as context. This has to match
  // the detail page, and it is what the public marketplace will rank on.
  const liveFrom = Number(deployment.liveFromBarTime) || 0;
  const liveTrades = useMemo(
    () => allTrades.filter((trade) => Number(trade.entryTime) >= liveFrom),
    [allTrades, liveFrom],
  );
  const stats = useMemo(() => summarizeSignalTrades(liveTrades), [liveTrades]);
  const seedCount = allTrades.length - liveTrades.length;

  // Return is measured from the equity at the moment of deployment, not from the original
  // deposit — otherwise the replayed history's P&L would leak into the live number.
  const baseline = useMemo(() => {
    const atDeploy = [...equity].reverse().find((point) => Number(point.barTime) <= liveFrom);
    return Number(atDeploy?.equity) || Number(deployment.virtualDeposit) || 10000;
  }, [equity, liveFrom, deployment.virtualDeposit]);
  const lastEquity = equity.length ? Number(equity[equity.length - 1].equity) : baseline;
  const changePct = baseline ? ((lastEquity - baseline) / baseline) * 100 : 0;
  const hasLiveHistory = liveTrades.length > 0;
  // A position can already be open at the moment of deployment, carried in by the seed replay.
  // It is backtest history — nobody was alerted to it and the copier will not take it — so it
  // must not be presented as a live position the signal just entered.
  const openPosition = allTrades.find((trade) => trade.status === 'open');
  const openIsCarriedOver = !!openPosition && Number(openPosition.entryTime) < liveFrom;

  return (
    <article className="signal-card">
      <header>
        <div>
          <h4>{deployment.name}</h4>
          <p className="muted-text">{deployment.symbol} · {deployment.timeframe}</p>
        </div>
        <StatusPill status={deployment.status} error={deployment.lastRunError} />
      </header>

      <SignalSparkline points={equity.map((point) => ({ t: point.barTime, v: point.equity }))} label="Equity" />

      <div className="signal-card-metrics">
        <div>
          <span className="muted-text">Return</span>
          <strong className={hasLiveHistory ? (changePct >= 0 ? 'up' : 'down') : ''}>
            {hasLiveHistory ? `${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%` : '—'}
          </strong>
        </div>
        <div>
          <span className="muted-text">Live trades</span>
          <strong>{stats.total}</strong>
        </div>
        <div>
          <span className="muted-text">Win rate</span>
          <strong>{stats.total ? `${stats.winRate.toFixed(0)}%` : '—'}</strong>
        </div>
        <div>
          <span className="muted-text">Net</span>
          <strong className={hasLiveHistory ? (stats.netProfit >= 0 ? 'up' : 'down') : ''}>
            {hasLiveHistory ? formatSignalMoney(stats.netProfit) : '—'}
          </strong>
        </div>
      </div>
      {!hasLiveHistory && (
        <p className="signal-card-seed-note muted-text">
          Tracking from today · {seedCount} earlier trade{seedCount === 1 ? '' : 's'} shown as
          backtest history
        </p>
      )}

      {openPosition ? (
        <p className={`signal-open-note${openIsCarriedOver ? ' is-carried' : ''}`}>
          <span className={`signal-dot ${openIsCarriedOver ? 'seed' : openPosition.side === 'BUY' ? 'up' : 'down'}`} />
          {openIsCarriedOver ? (
            <>
              Was already {openPosition.side === 'BUY' ? 'long' : 'short'} when deployed — from
              backtest history, not copied
            </>
          ) : (
            <>
              In a {openPosition.side === 'BUY' ? 'long' : 'short'} position from{' '}
              {Number(openPosition.entryPrice).toFixed(2)}
            </>
          )}
        </p>
      ) : (
        <p className="signal-open-note muted-text">Flat — waiting for the next setup</p>
      )}

      <footer>
        <span className="muted-text signal-countdown">
          {deployment.status === 'running' ? `Next check in ${countdown}` : 'Not evaluating'}
        </span>
        <div className="inline-actions">
          <button type="button" className="primary-cta compact-button" onClick={() => onOpen(deployment.id)}>
            Open
          </button>
          <button type="button" className="ghost-button compact-button" onClick={() => onToggle(deployment)}>
            {deployment.status === 'running' ? 'Pause' : 'Resume'}
          </button>
          <button type="button" className="ghost-button compact-button" onClick={() => onDelete(deployment)}>
            Delete
          </button>
        </div>
      </footer>
    </article>
  );
}

function SignalsTab({ user, savedStrategies, onUpgrade }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Read from the URL so a link can open a specific panel, but held in state so switching does
  // not push history entries — these are views of one page, not places you navigate back through.
  const [panel, setPanel] = useState(() => {
    const requested = new URLSearchParams(location.search).get('panel');
    return SIGNAL_PANELS.some((entry) => entry.id === requested) ? requested : 'signals';
  });
  const [deployments, setDeployments] = useState([]);
  const [details, setDetails] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [follows, setFollows] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [confirm, confirmDialog] = useConfirm();


  const isPremium = user?.testerPlan === 'premium' || !!user?.canExportMq5;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { deployments: rows } = await listDeployments();
      setDeployments(rows);
      // Card metrics need each deployment's ledger; fetched in parallel and tolerant of
      // individual failures so one broken signal cannot blank the whole tab.
      const loaded = await Promise.all(rows.map(async (row) => {
        try { return [row.id, await getDeployment(row.id)]; } catch (err) { return [row.id, null]; }
      }));
      setDetails(Object.fromEntries(loaded));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll the in-app feed. Polling rather than a socket because the production app runs under
  // Passenger, where a long-lived connection is not dependable.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { notifications: rows } = await listNotifications({ limit: 20 });
        if (!cancelled) setNotifications(rows);
      } catch (err) { /* the feed is a nicety; never surface its failures */ }
    };
    load();
    const timer = setInterval(load, SIGNAL_REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // Signals this user follows in a practice account. Free users can have these without ever
  // deploying anything, so the tab is useful to them from day one.
  useEffect(() => {
    listFollows().then((payload) => setFollows(payload.follows || [])).catch(() => setFollows([]));
  }, []);

  // Paid subscriptions to other people's signals. Independent of deployments and follows —
  // a user can have these without ever having deployed anything themselves.
  useEffect(() => {
    listSubscriptions()
      .then((payload) => setSubscriptions(payload.subscriptions || []))
      .catch(() => setSubscriptions([]));
  }, []);

  const handleToggle = async (deployment) => {
    const next = deployment.status === 'running' ? 'paused' : 'running';
    await updateDeployment(deployment.id, { status: next }).catch((err) => setError(err.message));
    refresh();
  };

  const handleDelete = async (deployment) => {
    const ok = await confirm({
      title: `Delete "${deployment.name}"?`,
      body: 'Its entire track record — trades, equity curve and history — is permanently '
        + 'removed. If you only want it to stop trading, pause it instead.',
      confirmLabel: 'Delete signal',
      tone: 'danger',
    });
    if (!ok) return;
    await deleteDeployment(deployment.id).catch((err) => setError(err.message));
    setFeedback('Signal deleted.');
    refresh();
  };

  // Only where a number means something. "Copy to MT5" is a setup screen, not a list, and a zero
  // beside it would read as an empty inbox rather than as something you have not set up yet.
  const panelCounts = {
    signals: deployments.length + follows.length + subscriptions.length,
    alerts: notifications.length,
  };

  return (
    <div className="section-card">
      <h3>Live signals</h3>
      <p className="muted-text">
        Run a saved strategy on live market data in the cloud and get notified the moment it
        trades — no MetaTrader, no VPS, nothing to leave running.
      </p>

      {feedback && <p className="success-text">{feedback}</p>}
      {error && <p className="signal-error">{error}</p>}

      <div className="inline-actions">
        {isPremium ? (
          <button
            type="button"
            className="primary-cta compact-button"
            onClick={() => setWizardOpen(true)}
            disabled={!savedStrategies?.length}
          >
            Deploy a signal
          </button>
        ) : (
          // The paywall is shown, not hidden — a free user should see exactly what they would
          // get before being asked to pay for it.
          <button type="button" className="primary-cta compact-button" onClick={onUpgrade}>
            Deploy a signal — VixPremium
          </button>
        )}
        {/* An EA is uploaded, analysed and (recommended) backtested on the import page, and the
            deploy wizard opens from there already holding it. Sending people straight to a deploy
            dialog would skip the step that tells them whether it even runs. */}
        <button
          type="button"
          className="ghost-button compact-button"
          onClick={() => navigate('/import-ea')}
        >
          Import an EA
        </button>
        <button type="button" className="ghost-button compact-button" onClick={refresh}>Refresh</button>
        <button type="button" className="ghost-button compact-button" onClick={() => navigate('/signals')}>
          Browse signals
        </button>
        <button type="button" className="ghost-button compact-button" onClick={() => navigate('/tester')}>
          Open Builder
        </button>
      </div>

      <nav className="signal-panel-tabs" aria-label="Signals sections">
        {SIGNAL_PANELS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`signal-panel-tab${panel === entry.id ? ' is-active' : ''}`}
            aria-current={panel === entry.id ? 'true' : undefined}
            onClick={() => setPanel(entry.id)}
          >
            <span>{entry.label}</span>
            {panelCounts[entry.id] > 0 && (
              <span className="signal-panel-count">{panelCounts[entry.id]}</span>
            )}
          </button>
        ))}
      </nav>

      {panel === 'signals' && (
        <>
      {!isPremium && (
        <div className="signal-upsell">
          <h4>What you get with a live signal</h4>
          <ul>
            <li>Your strategy runs on live data and is evaluated at every candle close.</li>
            <li>Telegram, email or in-app alerts with entry, stop, target and the reason it fired.</li>
            <li>A verified track record you can keep private or share publicly.</li>
          </ul>
        </div>
      )}

      {loading ? (
        <p className="muted-text">Loading your signals…</p>
      ) : deployments.length === 0 ? (
        <div className="signal-empty">
          <h4>No live signals yet</h4>
          <p className="muted-text">
            {savedStrategies?.length
              ? 'Pick one of your saved strategies and deploy it — it starts tracking immediately, with the last few months of history already filled in.'
              : 'Build and save a strategy in the Builder first, then deploy it here.'}
          </p>
        </div>
      ) : (
        <div className="signal-card-grid">
          {deployments.map((deployment) => (
            <DeploymentCard
              key={deployment.id}
              deployment={deployment}
              detail={details[deployment.id]}
              onOpen={(id) => navigate(`/signals/${id}`)}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {follows.length > 0 && (
        <div className="signal-following">
          {/* Distinct from the paid list below, which carried the same heading — two sections of
              cards under one title read as one list that had been split for no reason. */}
          <h4>Following on paper</h4>
          <div className="signal-card-grid">
            {follows.map(({ follow, ledger }) => (
              <article className="signal-card" key={follow.id}>
                <header>
                  <div>
                    <h4>{follow.name}</h4>
                    <p className="muted-text">
                      {follow.symbol} · {follow.timeframe} · by {follow.creatorName || 'Anonymous'}
                    </p>
                  </div>
                  <span className="signal-pill">Practice</span>
                </header>
                <div className="signal-card-metrics">
                  <div>
                    <span className="muted-text">Balance</span>
                    <strong>${Number(ledger.balance).toFixed(0)}</strong>
                  </div>
                  <div>
                    <span className="muted-text">Return</span>
                    <strong className={ledger.returnPct >= 0 ? 'up' : 'down'}>
                      {ledger.returnPct >= 0 ? '+' : ''}{ledger.returnPct.toFixed(1)}%
                    </strong>
                  </div>
                  <div>
                    <span className="muted-text">Trades</span>
                    <strong>{ledger.trades}</strong>
                  </div>
                  <div>
                    <span className="muted-text">Win rate</span>
                    <strong>{ledger.trades ? `${ledger.winRate.toFixed(0)}%` : '—'}</strong>
                  </div>
                </div>
                <footer>
                  <span className="muted-text signal-countdown">
                    {ledger.blownAt ? 'Account wiped out' : 'Following'}
                  </span>
                  <button
                    type="button"
                    className="primary-cta compact-button"
                    onClick={() => navigate(`/signals/${follow.deploymentId}`)}
                  >
                    Open
                  </button>
                </footer>
              </article>
            ))}
          </div>
        </div>
      )}

      {subscriptions.length > 0 && (
        <div className="signal-following">
          <h4>Signals you follow</h4>
          <div className="signal-card-grid">
            {subscriptions.map((subscription) => (
              <article className="signal-card" key={subscription.id}>
                <header>
                  <div>
                    <h4>{subscription.name}</h4>
                    <p className="muted-text">
                      {subscription.symbol} · {subscription.timeframe} · by{' '}
                      {subscription.creatorName || 'Anonymous'}
                    </p>
                  </div>
                  <span className="signal-pill ok">Following</span>
                </header>
                <div className="signal-card-metrics">
                  <div>
                    <span className="muted-text">Price</span>
                    <strong>${(Number(subscription.priceCents) / 100).toFixed(0)}/mo</strong>
                  </div>
                  <div>
                    <span className="muted-text">Since</span>
                    <strong>
                      {subscription.startedAt
                        ? new Date(subscription.startedAt).toLocaleDateString()
                        : '—'}
                    </strong>
                  </div>
                </div>
                {subscription.status === 'pending' && (
                  <p className="muted-text">
                    Waiting for PayPal to confirm. Access starts automatically.
                  </p>
                )}
                <footer>
                  <span className="muted-text signal-countdown">
                    {subscription.status === 'active' ? 'Alerts on' : 'Not active yet'}
                  </span>
                  <button
                    type="button"
                    className="primary-cta compact-button"
                    onClick={() => navigate(`/signals/${subscription.deploymentId}`)}
                  >
                    Open
                  </button>
                </footer>
              </article>
            ))}
          </div>
        </div>
      )}

        </>
      )}

      {panel === 'copier' && (
        <>
          {/* Shown whether or not anything is running yet. The copier takes several steps —
              downloading the EA, whitelisting the URL, pasting a token — and there is no reason to
              make someone deploy a signal first just to read them. */}
          <CopierPanel />
          {deployments.length === 0 && subscriptions.length === 0 && (
            <p className="muted-text">
              Nothing to copy yet. Deploy one of your own signals, or follow someone else's from{' '}
              <button type="button" className="signal-inline-link" onClick={() => navigate('/signals')}>
                the marketplace
              </button>
              , and the copier will pick it up on its next poll.
            </p>
          )}
        </>
      )}

      {panel === 'alerts' && (
        <>
          {/* Channels have to be set up BEFORE the first signal fires to be worth anything, so
              this sits at the top of its own panel rather than below the feed it feeds. */}
          <AlertSettingsPanel user={user} />

          <div className="signal-feed">
            <h4>Recent alerts</h4>
            {notifications.length === 0 ? (
              <p className="muted-text">
                No alerts yet. Every entry and exit your signals take will appear here, and on
                whichever channels you switch on above.
              </p>
            ) : (
              <ul>
                {notifications.slice(0, 20).map((item) => (
                  <li key={item.id}>
                    <button type="button" onClick={() => navigate(`/signals/${item.deploymentId}`)}>
                      <strong>{item.title}</strong>
                      <span className="muted-text">{new Date(item.createdAt).toLocaleString()}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {confirmDialog}

      {wizardOpen && (
        <DeploySignalWizard
          strategies={savedStrategies}
          onClose={() => setWizardOpen(false)}
          onDeployed={(deployment) => {
            setWizardOpen(false);
            setFeedback(`"${deployment.name}" is live.`);
            refresh();
            navigate(`/signals/${deployment.id}`);
          }}
        />
      )}
    </div>
  );
}

export default SignalsTab;
